import { useEffect, useState, useMemo } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, ChevronRight, ArrowLeft, Pencil, Trash2, X, Upload, Image as ImageIcon } from "lucide-react";

const MAX_IMG = 2 * 1024 * 1024;
const fileToBase64 = (file) =>
  new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file); });

function CategoryForm({ parent, initial, onClose, onSaved }) {
  const isSub = !!parent;
  const [f, setF] = useState(initial || { name: "", description: "", image: "", display_order: 0, active: true });
  const [busy, setBusy] = useState(false);

  const pickImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_IMG) return toast.error("Image too large — max 2 MB");
    setF({ ...f, image: await fileToBase64(file) });
  };

  const save = async (e) => {
    e.preventDefault();
    if (!f.name.trim()) return toast.error("Name is required");
    setBusy(true);
    try {
      const payload = {
        name: f.name, description: f.description, image: f.image,
        display_order: Number(f.display_order) || 0, active: f.active,
        parent_id: initial ? initial.parent_id : (parent ? parent.id : null),
      };
      if (initial?.id) await api.put(`/admin/categories/${initial.id}`, payload);
      else await api.post("/admin/categories", payload);
      toast.success(initial ? "Saved" : `${isSub ? "Subcategory" : "Category"} added`);
      onSaved();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Save failed"); }
    setBusy(false);
  };

  const title = initial ? `Edit ${isSub ? "Subcategory" : "Category"}` : `Add ${isSub ? "Subcategory" : "Category"}`;

  return (
    <div className="fixed inset-0 bg-foreground/60 z-50 flex items-start justify-center overflow-auto p-4 md:p-12">
      <form onSubmit={save} className="bg-background w-full max-w-lg border-2 border-foreground p-8 relative">
        <button type="button" onClick={onClose} className="absolute top-4 right-4"><X /></button>
        <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{parent ? parent.name : "Top-level"}</p>
        <h3 className="font-serif text-3xl mb-6 tracking-tighter">{title}</h3>

        <label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Name <span className="text-[hsl(var(--accent))]">*</span></label>
        <input data-testid="cat-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="e.g. Footwear" className="underline-input mb-5" required />

        <label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Description</label>
        <textarea data-testid="cat-desc" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })}
          placeholder="Short description" className="underline-input min-h-[70px] w-full mb-5" />

        <label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{isSub ? "Image (optional)" : "Category Image"}</label>
        <div className="flex items-center gap-3 mt-2 mb-5">
          <div className="w-16 h-16 bg-secondary border border-foreground/20 overflow-hidden flex items-center justify-center shrink-0">
            {f.image ? <img src={f.image} alt="" className="w-full h-full object-cover" /> : <ImageIcon size={18} className="text-muted-foreground" />}
          </div>
          <label className="flex items-center gap-2 border border-dashed border-foreground/40 px-4 py-2 cursor-pointer hover:border-foreground">
            <Upload size={14} /> <span className="font-mono text-[11px] uppercase tracking-wide">{f.image ? "Replace" : "Upload"}</span>
            <input type="file" accept="image/*" onChange={pickImage} className="hidden" />
          </label>
        </div>

        <div className="grid grid-cols-2 gap-4 mb-6">
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Display Order</label>
            <input data-testid="cat-order" type="number" value={f.display_order} onChange={(e) => setF({ ...f, display_order: e.target.value })} className="underline-input" />
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Status</label>
            <div className="flex gap-2 mt-2">
              {[["Active", true], ["Inactive", false]].map(([label, val]) => (
                <button type="button" key={label} onClick={() => setF({ ...f, active: val })}
                  className={`px-3 py-1.5 border font-mono text-[11px] uppercase tracking-wide ${f.active === val ? "bg-foreground text-background border-foreground" : "border-foreground/30 hover:border-foreground"}`}>
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="flex gap-3">
          <button type="button" onClick={onClose} className="flex-1 border border-foreground py-3 font-mono text-xs uppercase tracking-widest hover:bg-foreground hover:text-background">Cancel</button>
          <button data-testid="cat-save" type="submit" disabled={busy} className="flex-1 bg-foreground text-background py-3 font-mono text-xs uppercase tracking-widest hover:bg-[hsl(var(--accent))] disabled:opacity-50">
            {busy ? "Saving…" : `Save ${isSub ? "Subcategory" : "Category"}`}
          </button>
        </div>
      </form>
    </div>
  );
}

function AttributeForm({ categoryId, initial, onClose, onSaved }) {
  const [f, setF] = useState(initial || { name: "", type: "select", options: [], required: false, display_order: 0 });
  const [optInput, setOptInput] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    if (!f.name.trim()) return toast.error("Name is required");
    if (f.type !== "text" && f.options.length === 0) return toast.error("Add at least one option (or use type = text)");
    setBusy(true);
    try {
      const payload = { category_id: categoryId, name: f.name, type: f.type, options: f.options, required: f.required, display_order: Number(f.display_order) || 0 };
      if (initial?.id) await api.put(`/admin/attributes/${initial.id}`, payload);
      else await api.post("/admin/attributes", payload);
      toast.success(initial ? "Attribute saved" : "Attribute added");
      onSaved();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Save failed"); }
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 bg-foreground/60 z-50 flex items-start justify-center overflow-auto p-4 md:p-12">
      <form onSubmit={save} className="bg-background w-full max-w-lg border-2 border-foreground p-8 relative">
        <button type="button" onClick={onClose} className="absolute top-4 right-4"><X /></button>
        <h3 className="font-serif text-3xl mb-6 tracking-tighter">{initial ? "Edit Attribute" : "Add Attribute"}</h3>
        <label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Name <span className="text-[hsl(var(--accent))]">*</span></label>
        <input data-testid="attr-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="e.g. Upper Material" className="underline-input mb-5" required />
        <div className="grid grid-cols-2 gap-4 mb-5">
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Input Type</label>
            <select value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })} className="underline-input w-full">
              <option value="select">Select (single)</option>
              <option value="multiselect">Multi-select</option>
              <option value="text">Free text</option>
            </select>
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Display Order</label>
            <input type="number" value={f.display_order} onChange={(e) => setF({ ...f, display_order: e.target.value })} className="underline-input w-full" />
          </div>
        </div>
        {f.type !== "text" && (
          <div className="mb-5">
            <label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Options</label>
            <div className="flex gap-2 flex-wrap my-2">
              {f.options.map((o) => (
                <span key={o} className="px-2 py-1 border border-foreground text-xs font-mono flex items-center gap-2">{o}
                  <button type="button" onClick={() => setF({ ...f, options: f.options.filter((x) => x !== o) })}>×</button></span>
              ))}
            </div>
            <div className="flex gap-2">
              <input value={optInput} onChange={(e) => setOptInput(e.target.value)} placeholder="Add an option" className="underline-input"
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); if (optInput.trim()) { setF({ ...f, options: [...f.options, optInput.trim()] }); setOptInput(""); } } }} />
              <button type="button" onClick={() => { if (optInput.trim()) { setF({ ...f, options: [...f.options, optInput.trim()] }); setOptInput(""); } }} className="border px-3 text-xs font-mono uppercase">Add</button>
            </div>
          </div>
        )}
        <label className="flex items-center gap-3 text-sm mb-6"><input type="checkbox" checked={f.required} onChange={(e) => setF({ ...f, required: e.target.checked })} /> Required field</label>
        <div className="flex gap-3">
          <button type="button" onClick={onClose} className="flex-1 border border-foreground py-3 font-mono text-xs uppercase tracking-widest hover:bg-foreground hover:text-background">Cancel</button>
          <button data-testid="attr-save" type="submit" disabled={busy} className="flex-1 bg-foreground text-background py-3 font-mono text-xs uppercase tracking-widest hover:bg-[hsl(var(--accent))] disabled:opacity-50">{busy ? "Saving…" : "Save Attribute"}</button>
        </div>
      </form>
    </div>
  );
}

export default function CategoryManager() {
  const [all, setAll] = useState([]);
  const [drill, setDrill] = useState(null);   // selected top-level category id
  const [form, setForm] = useState(null);     // { parent, initial } | null
  const [attrs, setAttrs] = useState([]);
  const [attrForm, setAttrForm] = useState(null); // { initial } | null

  const load = async () => {
    try { setAll(await api.get("/admin/categories").then(r => r.data)); }
    catch (e) { toast.error("Failed to load categories"); }
  };
  useEffect(() => { load(); }, []);

  const loadAttrs = async (cid) => {
    try { setAttrs(await api.get(`/categories/${cid}/attributes`).then(r => r.data)); }
    catch (e) { setAttrs([]); }
  };
  useEffect(() => { if (drill) loadAttrs(drill); else setAttrs([]); }, [drill]);
  const delAttr = async (a) => {
    if (!window.confirm(`Delete attribute "${a.name}"?`)) return;
    try { await api.delete(`/admin/attributes/${a.id}`); toast.success("Deleted"); loadAttrs(drill); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Delete failed"); }
  };

  const tops = useMemo(() => all.filter(c => c.parent_id == null).sort((a, b) => a.display_order - b.display_order), [all]);
  const drillCat = useMemo(() => all.find(c => c.id === drill), [all, drill]);
  const subs = useMemo(() => all.filter(c => c.parent_id === drill).sort((a, b) => a.display_order - b.display_order), [all, drill]);

  const del = async (c) => {
    if (!window.confirm(`Delete "${c.name}"? This cannot be undone.`)) return;
    try { await api.delete(`/admin/categories/${c.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Delete failed"); }
  };

  const StatusPill = ({ active }) => (
    <span className={`font-mono text-[9px] uppercase tracking-widest px-2 py-0.5 border ${active ? "border-[hsl(var(--accent))] text-[hsl(var(--accent))]" : "border-foreground/30 text-muted-foreground"}`}>
      {active ? "Active" : "Inactive"}
    </span>
  );

  // ---- Drill-in: subcategories of one category ----
  if (drill && drillCat) {
    return (
      <div>
        <button onClick={() => setDrill(null)} className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-muted-foreground hover:text-foreground mb-4">
          <ArrowLeft size={13} /> All Categories
        </button>
        <div className="flex items-end justify-between mb-6 gap-4 flex-wrap">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Subcategories · {subs.length}</p>
            <h2 className="font-serif text-5xl tracking-tighter">{drillCat.name}.</h2>
          </div>
          <button data-testid="add-subcategory" onClick={() => setForm({ parent: drillCat, initial: null })}
            className="bg-foreground text-background px-5 py-2.5 font-mono text-[11px] uppercase tracking-widest hover:bg-[hsl(var(--accent))] flex items-center gap-2">
            <Plus size={13} /> Add Subcategory
          </button>
        </div>
        <table className="w-full text-sm">
          <thead><tr className="border-b border-foreground font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
            <th className="text-left py-2">Subcategory</th><th className="text-left">Products</th><th className="text-left">Status</th><th></th>
          </tr></thead>
          <tbody>
            {subs.map(s => (
              <tr key={s.id} className="border-b border-foreground/20">
                <td className="py-3 font-serif text-lg">{s.name}</td>
                <td className="font-mono">{s.product_count}</td>
                <td><StatusPill active={s.active} /></td>
                <td className="text-right">
                  <button onClick={() => setForm({ parent: drillCat, initial: s })} className="hover:text-[hsl(var(--accent))] mr-4"><Pencil size={14} /></button>
                  <button onClick={() => del(s)} className="hover:text-destructive"><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
            {subs.length === 0 && <tr><td colSpan={4} className="py-6 font-mono text-sm text-muted-foreground">No subcategories yet.</td></tr>}
          </tbody>
        </table>

        {/* Attributes — the dynamic fields a product in this category must fill */}
        <div className="mt-12 border-t border-foreground/20 pt-8">
          <div className="flex items-end justify-between mb-4 gap-4 flex-wrap">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Attributes · {attrs.length}</p>
              <p className="font-mono text-[10px] text-muted-foreground mt-1">Fields shown on the product form when a vendor picks {drillCat.name}.</p>
            </div>
            <button data-testid="add-attribute" onClick={() => setAttrForm({ initial: null })}
              className="bg-foreground text-background px-5 py-2.5 font-mono text-[11px] uppercase tracking-widest hover:bg-[hsl(var(--accent))] flex items-center gap-2">
              <Plus size={13} /> Add Attribute
            </button>
          </div>
          <table className="w-full text-sm">
            <thead><tr className="border-b border-foreground font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
              <th className="text-left py-2">Attribute</th><th className="text-left">Type</th><th className="text-left">Options</th><th className="text-left">Required</th><th></th>
            </tr></thead>
            <tbody>
              {attrs.map(a => (
                <tr key={a.id} className="border-b border-foreground/20">
                  <td className="py-3 font-serif text-lg">{a.name}</td>
                  <td className="font-mono text-xs uppercase">{a.type}</td>
                  <td className="text-[11px] text-muted-foreground max-w-xs truncate">{(a.options || []).join(", ") || "—"}</td>
                  <td className="font-mono text-xs">{a.required ? "Yes" : "—"}</td>
                  <td className="text-right">
                    <button onClick={() => setAttrForm({ initial: a })} className="hover:text-[hsl(var(--accent))] mr-4"><Pencil size={14} /></button>
                    <button onClick={() => delAttr(a)} className="hover:text-destructive"><Trash2 size={14} /></button>
                  </td>
                </tr>
              ))}
              {attrs.length === 0 && <tr><td colSpan={5} className="py-6 font-mono text-sm text-muted-foreground">No attributes yet — add fields like Upper Material, Closure, Fabric…</td></tr>}
            </tbody>
          </table>
        </div>

        {form && <CategoryForm parent={form.parent} initial={form.initial} onClose={() => setForm(null)} onSaved={() => { setForm(null); load(); }} />}
        {attrForm && <AttributeForm categoryId={drill} initial={attrForm.initial} onClose={() => setAttrForm(null)} onSaved={() => { setAttrForm(null); loadAttrs(drill); }} />}
      </div>
    );
  }

  // ---- Top-level categories list ----
  return (
    <div>
      <div className="flex items-end justify-between mb-6 gap-4 flex-wrap">
        <p className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">Categories · {tops.length}</p>
        <button data-testid="add-category" onClick={() => setForm({ parent: null, initial: null })}
          className="bg-foreground text-background px-5 py-2.5 font-mono text-[11px] uppercase tracking-widest hover:bg-[hsl(var(--accent))] flex items-center gap-2">
          <Plus size={13} /> Add Category
        </button>
      </div>
      <table className="w-full text-sm">
        <thead><tr className="border-b border-foreground font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
          <th className="text-left py-2">Category</th><th className="text-left">Subcategories</th><th className="text-left">Products</th><th className="text-left">Status</th><th></th>
        </tr></thead>
        <tbody>
          {tops.map(c => (
            <tr key={c.id} className="border-b border-foreground/20 group">
              <td className="py-3">
                <button onClick={() => setDrill(c.id)} className="flex items-center gap-3 text-left hover:text-[hsl(var(--accent))]">
                  <div className="w-10 h-10 bg-secondary border border-foreground/20 overflow-hidden flex items-center justify-center shrink-0">
                    {c.image ? <img src={c.image} alt="" className="w-full h-full object-cover" /> : <ImageIcon size={15} className="text-muted-foreground" />}
                  </div>
                  <span className="font-serif text-xl">{c.name}</span>
                </button>
              </td>
              <td className="font-mono">
                <button onClick={() => setDrill(c.id)} className="inline-flex items-center gap-1 hover:text-[hsl(var(--accent))]">
                  {c.subcategory_count} <ChevronRight size={13} />
                </button>
              </td>
              <td className="font-mono">{c.product_count}</td>
              <td><StatusPill active={c.active} /></td>
              <td className="text-right">
                <button data-testid={`manage-${c.id}`} onClick={() => setDrill(c.id)} className="text-xs font-mono uppercase hover:text-[hsl(var(--accent))] mr-4">Manage</button>
                <button onClick={() => setForm({ parent: null, initial: c })} className="hover:text-[hsl(var(--accent))] mr-4"><Pencil size={14} /></button>
                <button onClick={() => del(c)} className="hover:text-destructive"><Trash2 size={14} /></button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {form && <CategoryForm parent={form.parent} initial={form.initial} onClose={() => setForm(null)} onSaved={() => { setForm(null); load(); }} />}
    </div>
  );
}
